import { AxiosInstance } from "axios";

import { ICatchIssueUseCase } from '../catch-issue.usecase';
import { IFundComparisonBookmarkCreated } from '@mais-retorno-front/core-business/interfaces/fund-comparison-bookmark';
import { IColorHelper } from '@mais-retorno-front/core-business/interfaces/color';
import { IInMemoryRepo } from '@mais-retorno-front/core-business/interfaces/memory';
import { IBookmarkAssetDetail } from '@mais-retorno-front/models/bookmark-asset-detail.model';
import { MarketDataAPI } from "@core/market-data-sdk";

interface IHandleResponse {
  data: IBookmarkAssetDetail[];
  totalPages?: number;
  error?: boolean;
  message?: string;
}

interface IGetBookmarkAssets {
  handle(token: string, page: number, pageSize: number): Promise<IHandleResponse>;
}

export class GetBookmarkAssetsUseCase implements IGetBookmarkAssets {
  private ASSET_CLASS_COLORS = {
    Ações: '#d47500',
    Multimercado: '#008575',
    'Previdencia Privada': '#b70000',
    'Renda Fixa': '#00819e',
    Cambial: '#527ac2',
    default: '#90939E',
  };

  constructor(
    private readonly url: string,
    private readonly http: AxiosInstance,
    private readonly httpApi: MarketDataAPI,
    private readonly colorRepository: IColorHelper,
    private readonly memoryRepository: IInMemoryRepo<IFundComparisonBookmarkCreated>,
    private readonly catchIssueUseCase: ICatchIssueUseCase
  ) { }

  async handle(token: string, page: number, pageSize: number): Promise<IHandleResponse> {
    try {
      const { data: assets } = await this.http.get(this.url, {params: {page, limit: pageSize}});

      const assetsIdentifier = assets?.assets?.map((asset) => asset.asset.identifier);

      const newAssetsLoaded = (
        await Promise.all(
          assets?.assets
            ?.filter((asset) => asset.asset.identifier !== assetsIdentifier)
            .map(async (asset) => {
              try {
                const [statsData, header] = await Promise.all([
                  this.httpApi.funds.stats.getStatsDetails(asset?.asset?.identifier, {safe:true}),
                  this.httpApi.funds.getHeaderFull(asset?.asset?.identifier, {safe:true})
                ])

                return {
                  id: asset?.id,
                  identifier: asset?.asset?.identifier,
                  stats: {
                    networth: header?.networth,
                    quota_holders: header?.quota_holders,
                    sharpe_12_months: statsData?.stats?.timeframe?.last_12_months?.sharpe_ratio,
                    average_12_month_networth: statsData?.stats?.["12_months_average_networth"],
                    profitability: statsData?.stats?.timeframe?.begin?.profitability,
                    fund_volatility: statsData?.stats?.timeframe?.begin?.volatility
                  },
                  fund: {
                    class: header?.fund_class?.class?.name,
                    nicename: header?.nicename,
                    slug: header?.unique_slug,
                    identifier: header?.id
                  }
                }
              } catch (error) {
                this.catchIssueUseCase.handle({
                  error,
                  errorName: 'GetBookmarkAssetsUseCase',
                  extras: {
                    token
                  }
                });
                return null;
              }
            })
        )
      ).filter(Boolean);

      const assetsInMemory = assets?.assets
        ?.filter((asset) => asset.asset.identifier !== assetsIdentifier)
        ?.map((asset) => this.memoryRepository.getInMemory(asset.asset.identifier.toString()))
        ?.filter(Boolean);

      const allAssets = [...assetsInMemory, ...newAssetsLoaded];

      const assetsWithColor = allAssets?.map((asset) => {
        const hasColor = this.colorRepository.itemHasColor(asset?.id.toString());
        const color = !hasColor
          ? this.colorRepository.setColor(asset?.id.toString())
          : this.colorRepository.getColor(asset?.id.toString());

        return {
          color: this.ASSET_CLASS_COLORS[asset?.fund?.class] || this.ASSET_CLASS_COLORS.default,
          ...asset,
        };
      });

      return {
        data: assetsWithColor,
        totalPages: assets?.total,
      };
    } catch (error) {
      console.log(error)
      this.catchIssueUseCase.handle({
        error,
        errorName: 'GetBookmarkAssetsUseCase',
        extras: {
          token
        }
      });

      return {
        data: [],
        error: true,
        message: 'Não foi possível carregar suas comparações, tente novamente mais tarde'
      }
    }
  }
}